import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons'
import { formatCalendarDate } from '@lib/datetime'
import React, { useState } from 'react'
import {
	BlankCell,
	CalendarCard,
	CalendarHeader,
	DayCell,
	DayGrid,
	MonthNavButton,
	MonthTitle,
	WeekdayLetter,
	WeekdayRow,
} from './CreateTripCalendar.styles'
import { CreateTripDraft } from './core/draft'
import {
	dateForDay,
	daysInMonth,
	leadingBlankCount,
	monthContaining,
	monthTitle,
	nextMonth,
	previousMonth,
} from './core/month'
import { calendarDayState } from './core/presentation'
import { iconSizes } from '@/theme/tokens'
import { TestIds } from '@/testIds'

const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/** Six rows of seven keeps every month's height stable while flipping. */
const CELL_COUNT = 42

interface CreateTripCalendarProps {
	draft: CreateTripDraft
	onPickDate: (date: string) => void
	/** Trailing trip days no destination owns — the edit flow's; tinted neutral after the ranges. */
	uncoveredDays?: number
}

/**
 * Month calendar card for picking the start date, matching the mobile flow:
 * each destination's days are filled with an alternating tint laid out from
 * the start day, so picking a date shows the whole trip landing on the
 * calendar. The start day is just the first cell of its range — no ring, no bold.
 */
const CreateTripCalendar: React.FC<CreateTripCalendarProps> = ({
	draft,
	onPickDate,
	uncoveredDays = 0,
}) => {
	const [month, setMonth] = useState(() => monthContaining(draft.startDate))

	const leading = leadingBlankCount(month)
	const dayCount = daysInMonth(month)

	return (
		<CalendarCard>
			<CalendarHeader>
				<MonthNavButton
					type="button"
					aria-label="Previous month"
					onClick={() => setMonth(previousMonth(month))}
				>
					<ChevronLeftIcon sx={{ fontSize: iconSizes.lg }} />
				</MonthNavButton>
				<MonthTitle>{monthTitle(month)}</MonthTitle>
				<MonthNavButton
					type="button"
					aria-label="Next month"
					onClick={() => setMonth(nextMonth(month))}
				>
					<ChevronRightIcon sx={{ fontSize: iconSizes.lg }} />
				</MonthNavButton>
			</CalendarHeader>

			<WeekdayRow aria-hidden>
				{WEEKDAY_LETTERS.map((letter, index) => (
					<WeekdayLetter key={`${letter}-${index}`}>{letter}</WeekdayLetter>
				))}
			</WeekdayRow>

			<DayGrid>
				{Array.from({ length: CELL_COUNT }, (_, cell) => {
					const day = cell - leading + 1
					if (day < 1 || day > dayCount) return <BlankCell key={cell} />
					const date = dateForDay(month, day)
					const state = calendarDayState(draft, date, uncoveredDays)
					return (
						<DayCell
							key={cell}
							type="button"
							aria-label={formatCalendarDate(date, 'medium')}
							data-testid={TestIds.tripCreate.calendarDay(date)}
							$fill={state === null ? null : (state.colorIndex ?? 'uncovered')}
							onClick={() => onPickDate(date)}
						>
							{day}
						</DayCell>
					)
				})}
			</DayGrid>
		</CalendarCard>
	)
}

export default CreateTripCalendar
